/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import styles from './styles.module.scss';
import { useSingleReviewStatistic } from '@/features/reviews/api/getReviewStatistics';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { ReviewStatistics } from '../ReviewsOverview/ReviewStatistics/ReviewStatistics';
import { SubmitReviewButton } from '@/features/reviews/components/SubmitReviewButton/SubmitReviewButton';
import { type ProductModel } from '@/api/types/products-types';
import { Actionable, View } from '@az/starc-ui';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { scrollToCustomerReviews } from '../../utils/scrollToTitle';

const LazyOverviewPopup = dynamic(() =>
  import('../ReviewsOverview/OverviewPopup/OverviewPopup').then((mod) => mod.OverviewPopup)
);

type ProductStatisticsProps = {
  productDetails: ProductModel | undefined;
};

export const ProductStatistics = ({ productDetails }: ProductStatisticsProps) => {
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const skuNumber = productDetails?.itemId ?? '';
  const { data: reviewStatistic } = useSingleReviewStatistic({
    skuNumber,
    enabled: !!skuNumber,
  });

  const writeAReviewButton = useRef<HTMLButtonElement>(null);
  const anchor = useRef(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const popper = document.getElementById('product-reviews-popper');
      if (popper && !popper.contains(event.target as Node)) {
        setIsPopupOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClick = () => {
    if (isDesktop) {
      setIsPopupOpen((isOpen) => !isOpen);
    } else {
      scrollToCustomerReviews();
    }
  };

  return (
    <>
      {isPopupOpen ? (
        <LazyOverviewPopup
          handleClose={() => setIsPopupOpen(false)}
          anchor={anchor}
          writeAReviewButton={writeAReviewButton}
        />
      ) : null}
      <View className={styles.statisticsContainer}>
        {reviewStatistic ? (
          <Actionable onClick={handleClick} ref={anchor}>
            <ReviewStatistics reviewStatistic={reviewStatistic} showReviewCount />
          </Actionable>
        ) : null}
      </View>
      <div hidden>
        <SubmitReviewButton
          ref={writeAReviewButton}
          productId={productDetails?.itemId}
          productImage={productDetails?.itemImageUrl}
          productTitle={productDetails?.itemDescription}
          pageType="ProductDetail"
          label="Write review"
          variant="primary"
        />
      </div>
    </>
  );
};
